import React, {useState, useEffect} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import NavBar from "../pages/NavBar/NavBar";
import BackButton from "../../utils/BackButton";
import FooterData from "../pages/NavBar/FooterData";
import ExternalUserManagement from './ExternalUserManagement';
import InternalUserManagement from './InternalUserManagement';
import { checkUserAuthentication, handleUnauthenticatedUser } from "../../utils/authUtils";

// Interface for search state
interface SearchState {
    selectedOption1: string;
    selectedOption2: string;
    filter1Text: string;
    filter2Text: string;
    hasSearched: boolean;
    displayMode: 'requests' | 'search';
    searchResults: any[];
}

const AdminDashboard: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // User type selection state (Internal/External) - restore from location.state if available
    const initialUserType = location.state?.userTypeSelection || 'External';
    const [userTypeSelection, setUserTypeSelection] = useState<'Internal' | 'External'>(initialUserType);

    // Search state - restore from location.state if available
    const [searchState, setSearchState] = useState<SearchState | undefined>(
        location.state?.searchState
    );

    useEffect(() => {
        if (!checkUserAuthentication()) {
            handleUnauthenticatedUser();
            return;
        }
    }, []);

    // Function to handle user type selection change - clear search state when switching types
    const handleUserTypeSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as 'Internal' | 'External';
        setUserTypeSelection(value);
        // Clear search state when switching between Internal and External
        setSearchState(undefined);
    };

    const handleBackButtonClick = (event: React.MouseEvent) => {
        event.preventDefault();
        navigate('/internal-home');
    };

    return (
        <React.Fragment>
            <NavBar/>

            {/* Fixed container styling - removed problematic height constraint */}
            <div style={{
                marginLeft: '20px',
                marginRight: '20px',
                minHeight: '300px',
                position: 'relative',
                zIndex: 1
            }}>
                <div className="subheading grid-row">
                    <div className="grid-col flex-1">
                        <div style={{justifyContent: "flex-start"}}>
                            <BackButton onClick={handleBackButtonClick}/>
                        </div>

                        <h3 className="subheading-title" style={{
                            textAlign: 'center',
                            marginBottom: '20px',
                            marginTop: '20px'
                        }}>
                            User Management
                        </h3>

                        {/* User Type Selection Radio Buttons */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            marginBottom: '30px',
                            gap: '30px'
                        }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '500'
                            }}>
                                <input
                                    type="radio"
                                    name="userTypeSelection"
                                    value="Internal"
                                    checked={userTypeSelection === 'Internal'}
                                    onChange={handleUserTypeSelectionChange}
                                    style={{
                                        marginRight: '8px',
                                        cursor: 'pointer',
                                        transform: 'scale(1.2)'
                                    }}
                                />
                                Internal
                            </label>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '500'
                            }}>
                                <input
                                    type="radio"
                                    name="userTypeSelection"
                                    value="External"
                                    checked={userTypeSelection === 'External'}
                                    onChange={handleUserTypeSelectionChange}
                                    style={{
                                        marginRight: '8px',
                                        cursor: 'pointer',
                                        transform: 'scale(1.2)'
                                    }}
                                />
                                External
                            </label>
                        </div>
                    </div>
                </div>

                {/* Conditionally render the External view */}
                {userTypeSelection === 'External' && (
                    <ExternalUserManagement initialSearchState={searchState} />
                )}

                {/* Internal view - now implemented */}
                {userTypeSelection === 'Internal' && (
                    <InternalUserManagement initialSearchState={searchState} />
                )}
            </div>
            <FooterData
                styles={true}
            />
        </React.Fragment>
    );
};

export default AdminDashboard;